import { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { getApiUrl } from "@utils/baseUrl";
import { toast } from "react-toastify";
import { buyerCachingHandler, supplierCachingHandler, logisticsCachingHandler } from "@redux/CompanyProfile/companyProfileSlice";
import { cachingPanelData } from "@containers/CompanyProfile/utils/enum.js";
import { useAnalytics, createEventData } from "@core/analytics";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import HermesLoader from "@assets/images/HermesLoader.gif";
import { BRAND } from "@core/brand/brand-config";

const ProfileAISummary = ({ activePanelId }) => {
  const { trackEvent, setUser } = useAnalytics();
  const dispatch = useDispatch();
  const { logisticsProfile, companyProfile } = useSelector((state) => state);
  const [summaryData, setSummaryData] = useState({});
  const [loading, setLoading] = useState(true);
  const userDetails = useSelector((state) => state.userDetails);
  const userData = userDetails.userData;
  const email = userData.email;

  const company_id = companyProfile?.countDetails?.elastic_data_id?.[0] || logisticsProfile?.countDetails?.elastic_data_id?.[0] || "";

  const getSummary = useCallback(async () => {
    if (!company_id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    let reqObject = {
      profile_type: "company",
      category: activePanelId,
      company_id: company_id,
      persona: "importer-exporter",
    };
    const cacheKey = `${company_id}_${activePanelId}`;
    let cachedData = null;
    if (activePanelId === cachingPanelData.importers && companyProfile?.buyerCaching?.aiSummary?.[cacheKey]) {
      cachedData = companyProfile.buyerCaching.aiSummary[cacheKey];
    } else if (activePanelId === cachingPanelData.exporters && companyProfile?.supplierCaching?.aiSummary?.[cacheKey]) {
      cachedData = companyProfile.supplierCaching.aiSummary[cacheKey];
    } else if (activePanelId === cachingPanelData.logistic_partners && companyProfile?.logisticsCaching?.aiSummary?.[cacheKey]) {
      cachedData = companyProfile.logisticsCaching.aiSummary[cacheKey];
    }

    if (cachedData) {
      setSummaryData(cachedData);
      setLoading(false);

      return;
    }

    try {
      const response = await axios.post(getApiUrl("profileSummariser"), reqObject);
      trackEvent(
        activePanelId === "importers" ? trackingEventDictionary.fetchAISummaryAsBuyer : trackingEventDictionary.fetchAISummaryAsSupplier,
        createEventData("AI Summary", {
          url: window.location.pathname,
        })
      );
      setUser(email);
      const responseData = response.data;
      setSummaryData(responseData);
      if (responseData.narrative !== null && responseData.trendHighlights !== null) {
        if (activePanelId === cachingPanelData.importers) {
          let buyerCachingClone = JSON.parse(JSON.stringify(companyProfile.buyerCaching));
          if (!buyerCachingClone.aiSummary) {
            buyerCachingClone.aiSummary = {};
          }
          buyerCachingClone.aiSummary[cacheKey] = responseData;
          dispatch(buyerCachingHandler(buyerCachingClone));
        } else if (activePanelId === cachingPanelData.exporters) {
          let supplierCachingClone = JSON.parse(JSON.stringify(companyProfile.supplierCaching));
          if (!supplierCachingClone.aiSummary) {
            supplierCachingClone.aiSummary = {};
          }
          supplierCachingClone.aiSummary[cacheKey] = responseData;
          dispatch(supplierCachingHandler(supplierCachingClone));
        } else if (activePanelId === cachingPanelData.logistic_partners) {
          let logisticsCachingClone = JSON.parse(JSON.stringify(companyProfile.logisticsCaching));
          if (!logisticsCachingClone.aiSummary) {
            logisticsCachingClone.aiSummary = {};
          }
          logisticsCachingClone.aiSummary[cacheKey] = responseData;
          dispatch(logisticsCachingHandler(logisticsCachingClone));
        }
      } else {
        setSummaryData({});
        const emptyData = {};
        if (activePanelId === cachingPanelData.importers) {
          let buyerCachingClone = JSON.parse(JSON.stringify(companyProfile.buyerCaching));
          if (!buyerCachingClone.aiSummary) {
            buyerCachingClone.aiSummary = {};
          }
          buyerCachingClone.aiSummary[cacheKey] = emptyData;
          dispatch(buyerCachingHandler(buyerCachingClone));
        } else if (activePanelId === cachingPanelData.exporters) {
          let supplierCachingClone = JSON.parse(JSON.stringify(companyProfile.supplierCaching));
          if (!supplierCachingClone.aiSummary) {
            supplierCachingClone.aiSummary = {};
          }
          supplierCachingClone.aiSummary[cacheKey] = emptyData;
          dispatch(supplierCachingHandler(supplierCachingClone));
        } else if (activePanelId === cachingPanelData.logistic_partners) {
          let logisticsCachingClone = JSON.parse(JSON.stringify(companyProfile.logisticsCaching));
          if (!logisticsCachingClone.aiSummary) {
            logisticsCachingClone.aiSummary = {};
          }
          logisticsCachingClone.aiSummary[cacheKey] = emptyData;
          dispatch(logisticsCachingHandler(logisticsCachingClone));
        }
      }
    } catch (error) {
      toast.error("Something went wrong while fetching AI Summary. Please try again after sometime!!");
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [activePanelId, company_id, companyProfile, dispatch]);

  useEffect(() => {
    getSummary();
  }, [getSummary]);

  if (loading) {
    return (
      <div className={`tw-w-full tw-h-[calc(100vh-315px)] tw-flex tw-items-center tw-justify-center ${loading ? "tw-flex-col tw-gap-4" : ""}`}>
        <img src={HermesLoader} className="tw-w-40" alt="description of gif" />
        <span className="tw-text-xl tw-font-semibold -tw-mb-5">{BRAND.aiAssistantName} is analysing</span>
      </div>
    );
  }
  const enhanced = (key) => summaryData[key].replace(/<ul>/g, '<ul class="tw-flex tw-flex-col tw-gap-3">');

  return (
    <div className="tw-flex tw-flex-col">
      <div className={` tw-px-6 tw-border tw-border-gray-200 tw-rounded-lg tw-mt-4 tw-flex-grow tw-mx-3 ${Object.keys(summaryData)?.length === 0 ? "tw-w-full tw-h-[calc(100vh-245px)] tw-flex tw-items-center tw-justify-center" : "tw-grid tw-grid-cols-2 tw-divide-x tw-divide-gray-300 tw-gap-6"}`}>
        {Object.keys(summaryData)?.length > 0 ? (
          <>
            <div className="tw-pr-0 tw-py-6 tw-flex tw-flex-col tw-gap-2">
              <span className="tw-text-base tw-font-semibold">Company Summary</span>
              {/* <div className="tw-text-sm tw-font-normal" dangerouslySetInnerHTML={{ __html: summaryData.narrative }} /> */}
              <div className="tw-text-sm tw-font-normal" dangerouslySetInnerHTML={{ __html: enhanced("narrative") }} />
            </div>

            <div className="tw-pl-8 tw-py-6 tw-flex tw-flex-col tw-gap-2">
              <span className="tw-text-base tw-font-semibold">Trends Highlights</span>
              {/* <div className="tw-text-sm tw-font-normal" dangerouslySetInnerHTML={{ __html: summaryData.trendHighlights }} /> */}
              <div className="tw-text-sm tw-font-normal" dangerouslySetInnerHTML={{ __html: enhanced("trendHighlights") }} />
            </div>
          </>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-10 tw-items-center">
            <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1767707070/intel/misc/empty_state__for_hermes_ai_wjyaat.svg" alt="no-summary-found" />
            <span className="tw-text-base tw-font-semibold">No significant insights found</span>
          </div>
        )}
      </div>
      {Object.keys(summaryData)?.length > 0 && (
        <div className="tw-text-[#667085] tw-text-sm tw-font-medium tw-p-2 tw-pt-4 tw-flex tw-items-center">
          <span className="material-symbols-outlined mr-1">info</span>
          <span>AI Generated summary may not be 100% accurate all times. Use Human review for important decisions.</span>
        </div>
      )}
    </div>
  );
};

export default ProfileAISummary;
